import { cn } from "@gigpro/ui/tailwind/cn";
import type React from "react";

type TableSize = "default" | "compact";

const Table = ({
  ref,
  className,
  size = "default",
  ...props
}: React.ComponentProps<"table"> & { size?: TableSize }) => (
  <div className={cn("relative w-full overflow-auto", size === "default" ? "text-normal" : "text-small", className)}>
    <table ref={ref} className="group/table w-full caption-bottom" data-size={size} {...props} />
  </div>
);
Table.displayName = "Table";

const TableHeader = ({ ref, className, ...props }: React.ComponentProps<"thead">) => (
  <thead ref={ref} className={className} {...props} />
);
TableHeader.displayName = "TableHeader";

const TableBody = ({ ref, className, ...props }: React.ComponentProps<"tbody">) => (
  <tbody ref={ref} className={cn("[&_tr:last-child]:border-b-0", className)} {...props} />
);
TableBody.displayName = "TableBody";

const TableFooter = ({ ref, className, ...props }: React.ComponentProps<"tfoot">) => (
  <tfoot ref={ref} className={cn("border-t font-semibold [&>tr]:last:border-b-0", className)} {...props} />
);
TableFooter.displayName = "TableFooter";

const TableRow = ({ ref, className, ...props }: React.ComponentProps<"tr">) => (
  <tr ref={ref} className={cn("transition-colors data-[state=selected]:bg-brand-secondary/20", className)} {...props} />
);
TableRow.displayName = "TableRow";

const cellCompact = "group-data-[size=compact]/table:px-1.5 group-data-[size=compact]/table:py-1";

const TableHead = ({ ref, className, ...props }: React.ComponentProps<"th">) => (
  <th
    ref={ref}
    className={cn(
      "border-b border-light-gray px-3 py-2 text-left align-middle font-semibold has-[[role=checkbox]]:pr-0",
      cellCompact,
      className,
    )}
    {...props}
  />
);
TableHead.displayName = "TableHead";

const TableCell = ({ ref, className, ...props }: React.ComponentProps<"td">) => (
  <td
    ref={ref}
    className={cn(
      "border-b border-light-gray px-3 py-2 align-middle has-[[role=checkbox]]:pr-0",
      cellCompact,
      className,
    )}
    {...props}
  />
);
TableCell.displayName = "TableCell";

const TableCaption = ({ ref, className, ...props }: React.ComponentProps<"caption">) => (
  <caption ref={ref} className={cn("mt-3 text-small text-gray", className)} {...props} />
);
TableCaption.displayName = "TableCaption";

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
